/* eslint-disable @typescript-eslint/indent */
import { Button, FormControl, FormErrorMessage, FormLabel, useToast, VStack } from "@chakra-ui/react"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import { IBeaconSelector } from "../components"
import { AddIBeaconToZoneMutationVariables, useAddIBeaconToZoneMutation } from "../graphql"

type AddIBeaconToZoneFormValues = Pick<AddIBeaconToZoneMutationVariables, "ibeaconId">

const validationSchema = yup.object<Record<keyof AddIBeaconToZoneFormValues, yup.AnySchema<any, any, any> | Reference<unknown> | Lazy<any, any>>>({
	ibeaconId: yup.string().required().label("iBeacon"),
})

export type AddIBeaconToZoneFormProps = {
	zoneId: string
}

export const AddIBeaconToZoneForm: React.FC<AddIBeaconToZoneFormProps> = ({ zoneId }) => {
	const initialValues: AddIBeaconToZoneFormValues = {
		ibeaconId: "",
	}

	const [{ fetching }, addIBeacon] = useAddIBeaconToZoneMutation()

	const toast = useToast()
	const navigate = useNavigate()

	const onSubmit = async ({ ibeaconId }: AddIBeaconToZoneFormValues) => {
		const { data, error } = await addIBeacon({ zoneId, ibeaconId })

		if (error) {
			return toast({
				description: error.message.replace("[GraphQL] ", ""),
				status: "error",
			})
		}

		if (data?.addIBeaconToZone) {
			toast({
				description: "iBeacon added to zone",
				status: "success",
			})
			navigate(`/zones/${zoneId}`, { replace: true })

			return
		}
	}

	const formik = useFormik<AddIBeaconToZoneFormValues>({ initialValues, validationSchema, onSubmit })

	return (
		<VStack as="form" onSubmit={(e) => formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)} w="full" align="stretch" spacing={6}>
			<VStack w="full" align="stretch">
				<FormControl isInvalid={Boolean(formik.touched.ibeaconId && formik.errors.ibeaconId)}>
					<FormLabel fontWeight="bold">iBeacon</FormLabel>

					<IBeaconSelector value={formik.values.ibeaconId} onUpdate={(ibeaconId) => formik.setFieldValue("ibeaconId", ibeaconId || "")} />

					<FormErrorMessage>{formik.errors.ibeaconId}</FormErrorMessage>
				</FormControl>
			</VStack>
			<Button type="submit" colorScheme="primary" isLoading={fetching}>
				Add
			</Button>
		</VStack>
	)
}
